import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Pagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { saveAs } from "file-saver";
import { useLocation, useNavigate } from "react-router-dom";
import "../../styles/UserStyles.css";

import "../../styles/App.scss";
import {
  ADD_CUSTOMER,
  BOOKING_AIRPORT_DETAILS,
  EDIT_CUSTOMER,
  VIEW_CUSTOMER,
} from "../../component/common/Routes/AppRoutes";
import ReusableTable from "../../component/ui/ReusableTable";
import { customHeaders } from "./CustomHeaders";
import { customerColumnFetcher } from "../../Style";
import SearchAndButtonTab from "../../component/ui/SearchAndButtonTab";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCustomerRequest,
  fetchAllCustomersRequest,
  fetchCustomersRequest,
  resetCustomerState,
} from "../../redux/Customers/actions/customerActions";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loading from "../../component/ui/Loading";
import CustomButton from "../../component/ui/FormFields/Button";
import nodata from "../../assets/nodata.jpg";
import {
  VENDOR_ADD_CUSTOMER,
  VENDOR_BOOKING_AIRPORT_DETAILS,
  VENDOR_EDIT_CUSTOMER,
  VENDOR_VIEW_CUSTOMER,
} from "../../component/common/Routes/VendorRoutes";

import { fetchCustomersByVendorRequest } from "../../redux/Vendors/actions/vendorActions";
import axiosInstance from "../../redux/instance/AxiosInstance";

const CustomerManagementList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const pageNumber = location.state?.page ?? 1;
  const searchNavigateQuery = location.state?.searchQuery ?? "";

  const [searchQuery, setSearchQuery] = useState(searchNavigateQuery ?? "");
  const [searchColumn, setSearchColumn] = useState("");
  const [page, setPage] = useState(pageNumber);
  const rowsPerPage = 10;
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  console.log("page", page);

  const searchTimerRef = useRef(null);

  const user = useSelector((state) => state.auth.user);

  const permissions = useMemo(() => {
    let safePermissions = {};

    try {
      if (user?.permissions) {
        safePermissions =
          typeof user.permissions === "string"
            ? JSON.parse(user.permissions)
            : user.permissions;
      }
    } catch (error) {
      safePermissions = {};
    }

    return safePermissions;
  }, [user]);

  const {
    customers: response,
    success,
    loading,
    error,
  } = useSelector((state) =>
    user?.user_type === 2 ? state.vendors : state.customers,
  );

  const loadings = useSelector((state) => state.customers?.customers?.loading);

  const viewListLoading = loadings;
  const customers = response?.data || [];
  const totalRecords = response?.totalRecords || 0;
  const totalPages = response?.totalPages || 1;
  const deleteSuccess = success?.delete;
  const customerDetailsState = useSelector(
    (state) => state.customers?.customerDetails,
  );

  const deleteLoading = customerDetailsState?.deleteLoading;

  const filterMapping = {
    "Customer ID": "customer_serial_no",
    "Full Name": "full_name",
    Email: "email",
    Mobile: "phone",
  };

  const fetchCustomers = (
    pageNumber,
    search = searchQuery,
    searchBy = searchColumn,
  ) => {
    const params = {
      pagination_required: true,
      pagesize: rowsPerPage,
      pageNo: pageNumber,
    };

    if (search) {
      params.search = search;

      if (searchBy && searchBy.trim() !== "") {
        params.search_by = searchBy;
      }
    }

    if (user?.user_type === 2) {
      dispatch(resetCustomerState());
      dispatch(
        fetchCustomersByVendorRequest({
          ...params,
          vendor_id: user?.vendor_id,
        }),
      );
    } else {
      dispatch(resetCustomerState());
      dispatch(fetchCustomersRequest(params));
    }
  };

  const handleSearch = (query, column) => {
    setSearchQuery(query);

    if (column !== undefined) {
      setSearchColumn(column);
    }

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      fetchCustomers(1, query, column !== undefined ? column : searchColumn);
    }, 3000);
  };

  const handleColumnChange = (column) => {
    setSearchColumn(column);
  };

  const isInitialLoad = useRef(true);
  useEffect(() => {
    if (isInitialLoad.current && !searchNavigateQuery) {
      fetchCustomers(1, "", "");
      isInitialLoad.current = false;
      return;
    }

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    if (!searchNavigateQuery) {
      searchTimerRef.current = setTimeout(() => {
        setPage(1);
        fetchCustomers(1, searchQuery, searchColumn);
      }, 3000);
    } else {
      setPage(1);
      fetchCustomers(1, searchQuery, searchColumn);
    }

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchQuery, searchColumn]);

  useEffect(() => {
    if (!isInitialLoad.current) {
      fetchCustomers(page, searchQuery, searchColumn);
    }
  }, [page]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (deleteSuccess) {
      toast.success("Customer Deleted Successfully", {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          dispatch(resetCustomerState());
          fetchCustomers(page, searchQuery, searchColumn);
        },
      });
    }
  }, [deleteSuccess]);

  const [open, setOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  const handleExport = () => {
    const params = {
      pagination_required: false,
    };
    if (searchQuery) {
      params.search = searchQuery;

      if (searchColumn && searchColumn.trim() !== "") {
        params.search_by = searchColumn;
      }
    }

    const apiCall =
      user?.user_type === 2
        ? axiosInstance.get(
            `/customers/getcustomerbyvendor?vendor_id=${user.vendor_id}`,
            { params },
          )
        : axiosInstance.get("/customers/list", { params });

    apiCall
      .then((response) => {
        const allCustomersData = response?.data?.data || [];

        const csvHeaders = [
          "First Name,Last Name,Display Name,Email,Phone,Address1,Address2,City,State,Country,Pincode,Booking Count,Status",
        ];

        const csvRows = allCustomersData.map((customer) => {
          const escapeCsvField = (field) => {
            if (field === null || field === undefined) return '""';
            const stringField = String(field || "-");

            if (
              stringField.includes(",") ||
              stringField.includes('"') ||
              stringField.includes("\n")
            ) {
              return `"${stringField.replace(/"/g, '""')}"`;
            }
            return stringField;
          };

          return [
            escapeCsvField(customer.first_name),
            escapeCsvField(customer.last_name),
            escapeCsvField(`${customer.first_name} ${customer.last_name}`),
            escapeCsvField(customer.email),
            escapeCsvField(customer.phone),
            escapeCsvField(customer.address1),
            escapeCsvField(customer.address2),
            escapeCsvField(customer.city_name),
            escapeCsvField(customer.state_name),
            escapeCsvField(customer.country_name),
            escapeCsvField(customer.pincode),
            escapeCsvField(customer.booking_count),
            escapeCsvField(customer.status === 1 ? "Active" : "Inactive"),
          ].join(",");
        });

        const csvContent = [csvHeaders, ...csvRows].join("\n");
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        saveAs(blob, "customers.csv");
      })
      .catch((error) => {
        console.error("Export error:", error);
      });
  };

  const redirectToBooking = (customer) => {
    navigate(
      user?.role === "Vendor"
        ? VENDOR_BOOKING_AIRPORT_DETAILS
        : BOOKING_AIRPORT_DETAILS,
      {
        state: {
          customerDetails: customer,
        },
      },
    );
  };

  const handleOpenPopup = (customer) => {
    setCustomerToDelete(customer);
    setOpen(true);
  };

  const handleClosePopup = () => {
    setCustomerToDelete(null);
    setOpen(false);
  };

  const handleConfirmDelete = () => {
    if (customerToDelete) {
      setOpen(false);
      dispatch(deleteCustomerRequest(customerToDelete.id));
    }
    handleClosePopup();
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const transformedCustomers = useMemo(() => {
    return customers.map((customer) => ({
      customerId: customer?.customer_serial_no,
      id: customer?.id,
      full_name:
        customer.full_name ||
        `${customer.first_name || ""} ${customer.last_name || ""}`.trim(),
      email: customer?.email,
      mobile: customer?.phone,
      booking_count:
        customer?.booking_count > 0 ? customer?.booking_count : "0",
      lastActive: customer?.last_active !== null ? customer?.last_active : "-",

      createdDate: customer?.created_at,
      status: customer?.status === 1 ? "Active" : "Inactive",
    }));
  }, [customers]);

  // No need for client-side filtering if we're using server pagination
  const filteredCustomers = transformedCustomers;

  const sortedCustomers = useMemo(() => {
    if (!orderBy || !filteredCustomers.length) return filteredCustomers;

    return [...filteredCustomers].sort((a, b) => {
      let valA, valB;

      if (orderBy === "status") {
        valA = a[orderBy] === "Active" ? 1 : 0;
        valB = b[orderBy] === "Active" ? 1 : 0;
      } else if (orderBy === "lastActive" || orderBy === "createdDate") {
        valA = new Date(a[orderBy] || 0);
        valB = new Date(b[orderBy] || 0);
      } else if (orderBy === "customerId") {
        valA = Number(a[orderBy] || 0);
        valB = Number(b[orderBy] || 0);
      } else {
        valA = (a[orderBy] || "").toString().toLowerCase();
        valB = (b[orderBy] || "").toString().toLowerCase();
      }

      if (orderBy === "lastActive" || orderBy === "createdDate") {
        return order === "asc" ? valA - valB : valB - valA;
      } else if (typeof valA === "number" && typeof valB === "number") {
        return order === "asc" ? valA - valB : valB - valA;
      } else {
        if (valA < valB) return order === "asc" ? -1 : 1;
        if (valA > valB) return order === "asc" ? 1 : -1;
        return 0;
      }
    });
  }, [filteredCustomers, orderBy, order]);

  const columns = useMemo(() => {
    if (customers.length > 0) {
      const transformedCustomersForColumns = customers.map((customer) => ({
        customerId: customer?.customer_serial_no,
        id: customer?.id,
        fullName:
          customer.full_name ||
          `${customer.first_name || ""} ${customer.last_name || ""}`.trim(),
        email: customer?.email,
        mobile: customer?.phone,
      }));

      const columnData = customerColumnFetcher(transformedCustomersForColumns);

      if (Array.isArray(columnData)) {
        return columnData.filter((column) =>
          typeof column === "string" ? column !== "id" : column.value !== "id",
        );
      } else if (columnData && typeof columnData === "object") {
        const result = { ...columnData };

        if (Array.isArray(result[0])) {
          result[0] = result[0].filter((col) => col !== "id");
        }

        if (Array.isArray(result[1])) {
          result[1] = result[1].filter((col) => col.value !== "id");
        }

        return result;
      }
      return columnData;
    }
    return [[], []];
  }, [customers]);

  const customColumnOptions = useMemo(() => {
    return Object.entries(filterMapping).map(([label, value]) => ({
      label,
      value,
    }));
  }, [filterMapping]);

  // We're using server pagination, so just use the data directly
  const paginatedData = sortedCustomers;

  // The total pages should come from the server response
  const calculatedTotalPages = totalPages;

  if (viewListLoading) {
    return <Loading text="Loading Customer Details..." />;
  }

  return (
    <>
      <ToastContainer />
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <SearchAndButtonTab
                title="Customers"
                columns={customColumnOptions}
                hideSearchColumnSelector={true}
                searchColumn={searchColumn}
                setSearchColumn={handleColumnChange}
                searchQuery={searchQuery}
                setSearchQuery={handleSearch}
                handleExport={handleExport}
                permissions={permissions}
                user={user}
                routeName={
                  user?.role === "Vendor" ? VENDOR_ADD_CUSTOMER : ADD_CUSTOMER
                }
                type="customer"
              />
              {loading ? (
                <Box textAlign="center" py={5}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                  py={5}
                >
                  <img
                    src={nodata}
                    alt="No Data"
                    style={{ maxWidth: "300px", maxHeight: "300px" }}
                  />
                </Box>
              ) : sortedCustomers.length > 0 ? (
                <>
                  <ReusableTable
                    data={sortedCustomers}
                    headers={customHeaders(page)}
                    paginatedData={paginatedData}
                    redirectToBooking={redirectToBooking}
                    navigate={navigate}
                    title={
                      user?.user_type === 1 ||
                      user?.user_type === 4 ||
                      user?.user_type === 2
                        ? "Customer"
                        : ""
                    }
                    handleView={
                      permissions?.customers?.view ||
                      user?.user_type === 1 ||
                      user?.user_type === 2
                        ? (customer) => {
                            const viewRoute =
                              user?.role === "Vendor"
                                ? VENDOR_VIEW_CUSTOMER.replace(
                                    ":customerId",
                                    customer.id,
                                  )
                                : VIEW_CUSTOMER.replace(
                                    ":customerId",
                                    customer.id,
                                  );
                            navigate(viewRoute, {
                              state: { page: page, searchQuery: searchQuery },
                            });
                          }
                        : undefined
                    }
                    handleEdit={
                      permissions?.customers?.edit ||
                      user?.user_type === 1 ||
                      user?.user_type === 2
                        ? (customer) => {
                            const editRoute =
                              user?.role === "Vendor"
                                ? VENDOR_EDIT_CUSTOMER.replace(
                                    ":customerId",
                                    customer.id,
                                  )
                                : EDIT_CUSTOMER.replace(
                                    ":customerId",
                                    customer.id,
                                  );
                            navigate(editRoute, {
                              state: {
                                pageEdit: page,
                                searchQuery: searchQuery,
                              },
                            });
                          }
                        : undefined
                    }
                    handleOpenPopup={
                      permissions?.customers?.delete ||
                      user?.user_type === 1 ||
                      user?.user_type === 2
                        ? handleOpenPopup
                        : undefined
                    }
                    onSort={handleSort}
                    order={order}
                    orderBy={orderBy}
                  />

                  <Box className="button-box">
                    <Pagination
                      count={calculatedTotalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Box>
                </>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                  py={5}
                >
                  <img
                    src={nodata}
                    alt="No Data"
                    style={{ maxWidth: "300px", maxHeight: "300px" }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Dialog open={open} onClose={handleClosePopup}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete{" "}
              <strong>{customerToDelete?.full_name}</strong>?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={handleClosePopup}
              className="handle-back"
            >
              Cancel
            </Button>
            <CustomButton
              variant="contained"
              className="handle-delete"
              type="submit"
              size="small"
              loading={deleteLoading}
              text={"Delete"}
              onClick={handleConfirmDelete}
            />
          </DialogActions>
        </Dialog>
      </Grid>
    </>
  );
};
export default CustomerManagementList;
